import React, { useMemo, useCallback, useState } from 'react';
import { Component } from '@/types';
import { shouldBlockSensorControl, useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import * as LucideIcons from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PushButtonThemeConfig {
  releaseColor: string;
  pressColor: string;
  glowColor: string;
  glowIntensity: number;
  shape: 'circular' | 'pill' | 'rounded-rect';
  borderColor: string;
  textColor: string;
}

interface PushButtonProps {
  component: Component;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THEMES: Record<string, PushButtonThemeConfig> = {
  power: {
    releaseColor: '#2a2a2a',
    pressColor: '#ff3333',
    glowColor: '#ff0000',
    glowIntensity: 8,
    shape: 'circular',
    borderColor: '#444444',
    textColor: '#ffffff',
  },
  circular: {
    releaseColor: '#4a90d9',
    pressColor: '#357abd',
    glowColor: '#4a90d9',
    glowIntensity: 6,
    shape: 'circular',
    borderColor: '#3a7bc8',
    textColor: '#ffffff',
  },
  pill: {
    releaseColor: '#6c63ff',
    pressColor: '#5a52d5',
    glowColor: 'none',
    glowIntensity: 0,
    shape: 'pill',
    borderColor: '#5a52d5',
    textColor: '#ffffff',
  },
  material: {
    releaseColor: '#1976D2',
    pressColor: '#1565C0',
    glowColor: 'none',
    glowIntensity: 0,
    shape: 'rounded-rect',
    borderColor: '#1565C0',
    textColor: '#ffffff',
  },
  neon: {
    releaseColor: 'transparent',
    pressColor: '#00ff88',
    glowColor: '#00ff88',
    glowIntensity: 12,
    shape: 'circular',
    borderColor: '#00ff88',
    textColor: '#00ff88',
  },
};

// ─── Size Presets ────────────────────────────────────────────────────────────

const SIZE_MAP: Record<string, number> = { sm: 60, md: 80, lg: 100 };

// ─── Component ───────────────────────────────────────────────────────────────

export const PushButton: React.FC<PushButtonProps> = ({ component }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;
  const [isPressed, setIsPressed] = useState(false);

  // ─── Theme Resolution ────────────────────────────────────────────────
  const themeName = (properties.theme as string) ?? 'power';
  const sizeName = (properties.size as string) ?? 'md';
  const disabled = (properties.disabled as boolean) ?? false;
  const label = (properties.label as string) ?? '';
  const shapeProp = properties.shape as string | undefined;

  const theme = useMemo(() => {
    const base = THEMES[themeName] ?? THEMES['power'];
    return {
      ...base,
      ...(properties.pressColor != null && { pressColor: properties.pressColor as string }),
      ...(properties.releaseColor != null && { releaseColor: properties.releaseColor as string }),
      ...(properties.glowColor != null && { glowColor: properties.glowColor as string }),
      ...(properties.glowIntensity != null && { glowIntensity: properties.glowIntensity as number }),
      ...(properties.borderColor != null && { borderColor: properties.borderColor as string }),
      ...(properties.textColor != null && { textColor: properties.textColor as string }),
      ...(shapeProp != null && { shape: shapeProp as 'circular' | 'pill' | 'rounded-rect' }),
    };
  }, [themeName, properties.pressColor, properties.releaseColor, properties.glowColor, properties.glowIntensity, properties.borderColor, properties.textColor, shapeProp]);

  const btnSize = SIZE_MAP[sizeName] ?? 80;

  // ─── Icon Resolution ──────────────────────────────────────────────────
  const IconComponent = useMemo(() => {
    const iconName = properties.icon as string | undefined;
    if (!iconName) return null;
    return (LucideIcons as Record<string, unknown>)[iconName] as React.FC<{ size?: number; color?: string }> | undefined;
  }, [properties.icon]);

  // ─── Interaction ─────────────────────────────────────────────────────
  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';
  const sensorControlBlocked = shouldBlockSensorControl(component);

  const sensorId = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceId as string || component.id;
    }
    return component.id;
  }, [properties.action, component.id]);

  // Check if a device is bound
  const deviceBinding = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceBinding as import('@/components/shared/rendering/InteractionContext').DeviceBinding | undefined;
    }
    return undefined;
  }, [properties.action]);

  const canPress = !disabled && !sensorControlBlocked && Boolean(deviceBinding || (isViewerMode && handlers.onSensorControl));

  const handlePointerDown = useCallback(async () => {
    if (!canPress) return;
    setIsPressed(true);

    // Device-bound: toggle device directly (works in editor + viewer)
    if (deviceBinding) {
      const { executeDeviceCommand } = await import('@/integrations/kasa/deviceCommandMapper');
      executeDeviceCommand(
        { sensorId, action: 'control', parameters: { command: 'press', value: true } },
        deviceBinding
      );
      return;
    }

    if (!isViewerMode) return;
    handlers.onSensorControl?.({
      sensorId,
      action: 'control',
      parameters: { command: 'press', value: true },
    });
  }, [canPress, deviceBinding, isViewerMode, handlers, sensorId]);

  const handlePointerUp = useCallback(() => {
    if (!canPress) return;
    setIsPressed(false);

    if (deviceBinding) return; // Device command already sent on press

    if (!isViewerMode) return;
    handlers.onSensorControl?.({
      sensorId,
      action: 'control',
      parameters: { command: 'release', value: false },
    });
  }, [canPress, deviceBinding, isViewerMode, handlers, sensorId]);

  // ─── Computed Styles ──────────────────────────────────────────────────
  const currentColor = isPressed ? theme.pressColor : theme.releaseColor;
  const hasGlow = theme.glowColor !== 'none' && theme.glowIntensity > 0;
  const glowShadow = hasGlow && isPressed
    ? `0 0 ${theme.glowIntensity * 2}px ${theme.glowColor}, 0 0 ${theme.glowIntensity}px ${theme.glowColor}`
    : hasGlow
    ? `0 0 ${theme.glowIntensity}px ${theme.glowColor}40`
    : 'none';

  const isCircular = theme.shape === 'circular';
  const isPill = theme.shape === 'pill';

  const borderRadius = isCircular ? '50%' : isPill ? '999px' : '12px';

  const buttonStyle: React.CSSProperties = {
    // Circular: square constrained by the smaller axis
    // Pill/Rounded-rect: fill width, proportional height
    ...(isCircular
      ? { height: '100%', aspectRatio: '1 / 1', maxWidth: '100%' }
      : { width: '100%', height: isPill ? '60%' : '70%' }),
    maxHeight: '100%',
    borderRadius,
    backgroundColor: currentColor,
    border: `2px solid ${theme.borderColor}`,
    boxShadow: glowShadow,
    cursor: canPress ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s ease',
    transform: isPressed ? 'scale(0.95)' : 'scale(1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    userSelect: 'none',
  };

  const iconSize = Math.round(btnSize * 0.35);

  return (
    <div
      data-testid="push-button"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        gap: 6,
        userSelect: 'none',
      }}
    >
      <div
        role="button"
        aria-pressed={isPressed}
        aria-disabled={!canPress}
        aria-label={label || 'Push button'}
        tabIndex={canPress && isViewerMode ? 0 : -1}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => {
          if (isPressed) {
            setIsPressed(false);
            if (canPress && !deviceBinding) {
              handlers.onSensorControl?.({
                sensorId,
                action: 'control',
                parameters: { command: 'release', value: false },
              });
            }
          }
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canPress && isViewerMode) {
            e.preventDefault();
            handlePointerDown();
          }
        }}
        onKeyUp={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canPress && isViewerMode) {
            e.preventDefault();
            handlePointerUp();
          }
        }}
        style={buttonStyle}
      >
        {IconComponent && (
          <IconComponent size={iconSize} color={theme.textColor} />
        )}
        {label && (
          <span style={{
            fontSize: Math.max(10, btnSize * 0.16),
            fontWeight: 600,
            color: theme.textColor,
            lineHeight: 1.2,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 16px)',
          }}>
            {label}
          </span>
        )}
      </div>
    </div>
  );
};
